import { useContext } from "react";
import { RyeSkeleton } from "../rye/RyeSkeleton";
import { BreadAccount } from "breadcommon";
import { AccountsContext } from "../firebaseio/AccountsContext";
import { useExchangeRates } from "../firebaseio/ExchangeRatesContext";
import { LoadingInitialDataContext } from "../LoadUser";
import { RyeScreenWrapper } from "../rye/RyeScreenWrapper";
import { RyeMainContentPanel } from "../rye/RyeMainContentPanel";
import { RyeEmptyState } from "../rye/RyeEmptyState";
import { RyeButton } from "../rye/RyeButton";
import { useRyeModal } from "../rye/RyeModal";
import { DollarAmount } from "../common/DollarAmount";
import { AccountIdentity } from "../common/AccountIdentity";
import { AddNewAccountModal } from "../accounts_screen/AddNewAccountModal";
import { useOpenSettings } from "../settings_screen/settingsDefinitions";
import {
  BalanceGroup,
  computeNetWorth,
  groupAccountBalances,
} from "../derived/accountBalances";
import { RyeCard } from "../rye/RyeCard";
import { buildClasses } from "../utils/buildClasses";
import { MAIN_NAV_DEFINITIONS } from "../main_nav/mainNavDefinitions";

export function BalancesScreen(): JSX.Element {
  const accounts = useContext(AccountsContext);
  const loading = useContext(LoadingInitialDataContext);
  const { convertLatest, isLoadingLatest } = useExchangeRates();
  const openSettings = useOpenSettings();
  const { openModal } = useRyeModal();

  const groups = groupAccountBalances(accounts, convertLatest);
  // Totals are converted into the preferred currency, so they wait for both.
  const isLoading = loading.accounts || loading.preferences || isLoadingLatest;
  const netWorth = computeNetWorth(accounts, convertLatest);

  function linkAccount() {
    openSettings("accounts");
    openModal(<AddNewAccountModal />);
  }

  return (
    <RyeScreenWrapper>
      <RyeMainContentPanel
        title="Balances"
        // Only with balances to show: the empty state fills the panel's height
        // to center itself, which the centered column doesn't give it.
        centerContent={isLoading || groups.length > 0}
        actions={
          !isLoading &&
          groups.length > 0 && (
            <RyeButton
              text="Manage accounts"
              icon="settings"
              size="sm"
              variant="outlined"
              vibe="subdued"
              shadow="sm"
              onClick={() => openSettings("accounts")}
            />
          )
        }
      >
        {isLoading ? (
          <BalancesSkeleton />
        ) : groups.length === 0 ? (
          <RyeEmptyState
            icon={MAIN_NAV_DEFINITIONS.BALANCES.icon}
            title="Your account balances will show up here"
            description="Link your financial accounts, and Bread will sync your account balances automatically."
            actions={
              <RyeButton
                text="Link an account"
                icon="add"
                vibe="primary"
                variant="filled"
                onClick={linkAccount}
              />
            }
          />
        ) : (
          <div className={buildClasses("flex", "flex-col", "gap-6")}>
            <RyeCard label="Net total">
              <div
                className={buildClasses(
                  "flex",
                  "items-baseline",
                  "justify-between",
                  "gap-4",
                )}
              >
                <h2 className={buildClasses("text-lg", "text-on-surface-700")}>
                  Net total
                </h2>
                <div
                  className={buildClasses("shrink-0", {
                    if: netWorth < 0,
                    then: "text-red-600",
                    else: "text-on-surface-900",
                  })}
                >
                  <DollarAmount
                    n={netWorth}
                    showMinus
                    showDollarSign
                    size="lg"
                  />
                </div>
              </div>
            </RyeCard>
            {groups.map((group) => (
              <BalanceGroupSection key={group.id} group={group} />
            ))}
          </div>
        )}
      </RyeMainContentPanel>
    </RyeScreenWrapper>
  );
}

function BalancesSkeleton(): JSX.Element {
  return (
    <div
      role="status"
      aria-label="Loading balances"
      className={buildClasses("flex", "flex-col", "gap-6")}
    >
      <RyeCard label="Net total" rise={false}>
        <div
          className={buildClasses(
            "flex",
            "items-center",
            "justify-between",
            "gap-4",
          )}
        >
          <h2 className={buildClasses("text-lg", "text-on-surface-700")}>
            Net total
          </h2>
          <RyeSkeleton className={buildClasses("h-6", "w-28")} />
        </div>
      </RyeCard>
      {[3, 2].map((rows, i) => (
        <RyeCard key={i} rise={false}>
          <div
            className={buildClasses(
              "flex",
              "items-center",
              "justify-between",
              "mb-2",
            )}
          >
            <RyeSkeleton className={buildClasses("h-4", "w-32")} />
            <RyeSkeleton className={buildClasses("h-4", "w-20")} />
          </div>
          <ul className={buildClasses("divide-y", "divide-on-surface-200")}>
            {Array.from({ length: rows }, (_, j) => (
              <li
                key={j}
                className={buildClasses(
                  "flex",
                  "items-center",
                  "gap-3",
                  "py-3",
                )}
              >
                <RyeSkeleton
                  shape="circle"
                  className={buildClasses("size-6", "shrink-0")}
                />
                <RyeSkeleton className={buildClasses("h-4", "w-40")} />
                <RyeSkeleton
                  className={buildClasses("h-4", "w-20", "ml-auto")}
                />
              </li>
            ))}
          </ul>
        </RyeCard>
      ))}
    </div>
  );
}

function BalanceGroupSection({ group }: { group: BalanceGroup }): JSX.Element {
  return (
    <RyeCard
      title={group.label}
      trailing={<DollarAmount n={group.total} showMinus showDollarSign />}
    >
      <ul className={buildClasses("divide-y", "divide-on-surface-200")}>
        {group.accounts.map((account) => (
          <AccountBalanceRow key={account.id} account={account} />
        ))}
      </ul>
    </RyeCard>
  );
}

function AccountBalanceRow({
  account,
}: {
  account: BreadAccount;
}): JSX.Element {
  return (
    <li
      className={buildClasses(
        "flex",
        "items-center",
        "justify-between",
        "gap-4",
        "py-3",
      )}
    >
      <AccountIdentity account={account} />
      {/* DollarAmount fills its container, so give it only what it needs. */}
      <div className={buildClasses("shrink-0")}>
        <DollarAmount
          n={account.current_balance ?? 0}
          showMinus
          showDollarSign
          currencyCode={account.currency_code}
        />
      </div>
    </li>
  );
}
